export type {
  CampaignGroup,
  ChartConfig,
  ChartFilters,
  ChartResult,
  GridChartCell,
  GridChartResult,
  LineChartPoint,
  LineChartResult,
  LineChartSeries,
  MetricDenominator,
  MetricNumerator,
  SendInsightDimension,
} from "@hightouch/lib/customer-data/decision-engine/send-insights/types";

import type {
  ChartConfig,
  MetricDenominator,
  MetricNumerator,
  SendInsightDimension,
} from "@hightouch/lib/customer-data/decision-engine/send-insights/types";
import type { DecisionEngineChannelType } from "@hightouch/lib/customer-data/decision-engine/types";

export type ChartType = "line" | "grid" | "channel-reach-line";

export type DisplayFormat = "absolute" | "percentage" | "ratio";

export type SendInsightTab = "fidelity" | "comparison";

export interface SendInsightLabel {
  name: string;
  id?: string;
  url?: string;
  channelType?: DecisionEngineChannelType;
  campaignIds?: string[];
}

export type LabelResolver = (
  dimension: SendInsightDimension,
  rawValue: string | number,
) => SendInsightLabel;

export const DIMENSION_LABELS: Record<SendInsightDimension, string> = {
  none: "None",
  channel_type: "Channel type",
  channel: "Channel",
  experiment: "Experiment",
  message_id: "Message",
  campaign_group: "Campaign group",
  match_status: "Match status",
  day_of_week: "Day of week",
  time_bucket: "Time of day",
};

export interface MetricPreset {
  label: string;
  numerator: MetricNumerator;
  denominator: MetricDenominator;
  rolling_window_days?: number;
}

const COMPARISON_PRESETS: Record<string, MetricPreset> = {
  send_count: {
    label: "Send count",
    numerator: "sends",
    denominator: "none",
  },
  sends_per_user: {
    label: "Weekly sends per user",
    numerator: "sends",
    denominator: "rolling_30d_users",
    rolling_window_days: 7,
  },
};

const FIDELITY_PRESETS: Record<string, MetricPreset> = {
  match_rate: {
    label: "Match rate",
    numerator: "matched",
    denominator: "recs",
  },
  match_count: {
    label: "Match count",
    numerator: "matched",
    denominator: "none",
  },
  rec_count: {
    label: "Rec count",
    numerator: "recs",
    denominator: "none",
  },
  recs_per_user: {
    label: "Weekly recs per user",
    numerator: "recs",
    denominator: "rolling_30d_users",
    rolling_window_days: 7,
  },
  matches_per_user: {
    label: "Weekly matches per user",
    numerator: "matched",
    denominator: "rolling_30d_users",
    rolling_window_days: 7,
  },
};

export function getMetricPresets(
  metadataColumns: string[],
  tab: SendInsightTab,
): Record<string, MetricPreset> {
  const base = tab === "fidelity" ? FIDELITY_PRESETS : COMPARISON_PRESETS;
  const presets: Record<string, MetricPreset> = { ...base };
  for (const col of metadataColumns) {
    presets[`metadata_${col}`] = {
      label: `${col} rate`,
      numerator: `metadata.${col}`,
      denominator: "sends",
    };
  }
  return presets;
}

export function extractMetadataColumns(charts: ChartConfig[]): string[] {
  const cols = new Set<string>();
  for (const c of charts) {
    if (c.numerator.startsWith("metadata.")) {
      cols.add(c.numerator.slice("metadata.".length));
    }
  }
  return [...cols];
}

export function getDisplayFormat(
  denominator: MetricDenominator,
): DisplayFormat {
  if (denominator === "none") return "absolute";
  if (denominator === "rolling_30d_users") return "ratio";
  return "percentage";
}
